<script setup lang="ts">
import { Dialog } from "../../../../base-components/Headless";
import {
  FormLabel,
  FormInput,
  FormCheck,
  FormSelect,
  FormSwitch,
  FormTextarea
} from "../../../../base-components/Form";
import Button from "../../../../base-components/Button";
import LoadingIcon from "../../../../base-components/LoadingIcon";
import Lucide from "../../../../base-components/Lucide/Lucide.vue";
import Table from "../../../../base-components/Table";
import {
  computed,
  reactive,
  ref,
  toRefs,
  watch,
  onBeforeMount,
  onBeforeUnmount
} from "vue";
import { useProductosStore } from "../../../../services/productos";
import { useMensajesStore } from "../../../../services/mensajes";
import type { LogAcciones, Producto } from "@fixeduy/restaurantestypes";
import { useTasasIVAStore } from "../../../../services/tasas";
import { userUserStore } from "../../../../stores/user";
import { round } from "lodash";
import { helpers, maxLength, required } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import InputNumero from "../../../../custom-components/InputNumero/InputNumero.vue";
import { useMonedasStore } from "../../../../services/monedas";
import Alert from "../../../../base-components/Alert/Alert.vue";
import { useProductosCategoriasStore } from "../../../../services/productosCategorias";
import TomSelect from "../../../../custom-components/TomSelect/TomSelect.vue";
import { storeToRefs } from "pinia";
import { getCurrentTime } from "../../../../utils/fecha";
import { useLogAccionesStore } from "../../../../services/logAcciones";

interface Props {
  producto: Producto;
}

const props = defineProps<Props>();
const emit = defineEmits<{ (e: "cerrar"): void }>();

const productosStore = useProductosStore();
const mensajesStore = useMensajesStore();
const tasasStore = useTasasIVAStore();
const userStore = userUserStore();
const monedasStore = useMonedasStore();
const categoriasStore = useProductosCategoriasStore();
const logAccionesStore = useLogAccionesStore();

// const cargando = ref(false);
const { cargando } = storeToRefs(productosStore);
const fotoFile = ref<File | null>(null);

const productoActual = computed(
  () => productosStore.getById(props.producto.id) ?? props.producto
);

const quitarFotoMarcado = ref(false);
const fotoPreviewObjectUrl = ref<string | null>(null);
const fotoUrlPreview = computed(() => {
  if (quitarFotoMarcado.value) return null;
  if (fotoPreviewObjectUrl.value) return fotoPreviewObjectUrl.value;
  return productoActual.value.fotoUrl;
});

const setFotoPreviewFromFile = () => {
  if (fotoPreviewObjectUrl.value) {
    URL.revokeObjectURL(fotoPreviewObjectUrl.value);
    fotoPreviewObjectUrl.value = null;
  }

  if (fotoFile.value) {
    fotoPreviewObjectUrl.value = URL.createObjectURL(fotoFile.value);
  }
};

onBeforeUnmount(() => {
  if (fotoPreviewObjectUrl.value) {
    URL.revokeObjectURL(fotoPreviewObjectUrl.value);
    fotoPreviewObjectUrl.value = null;
  }
});

onBeforeMount(async () => {
  // En testing/deploys puede que el store persistido arranque vacío.
  if (tasasStore.tasas.length === 0) await tasasStore.getTasasFromFirestore();
});

const formData = reactive({
  codigo: props.producto.codigo ?? "",
  nombre: props.producto.nombre ?? "",
  observaciones: props.producto.observaciones ?? "",
  activo: props.producto.activo ?? true,
  categoriaId: props.producto.categoriaId ?? "",
  tasaId:
    props.producto.tasaIvaId ?? userStore.opcionesGenerales.tasaIVADefecto,
  precioSinIva: props.producto.precioSinIva ?? 0,
  precioConIva: props.producto.precioConIva ?? 0,
  moneda: userStore.opcionesGenerales.monedaDefecto,
  impresionSectores: props.producto.impresionSectores ?? []
});

const rules = {
  codigo: {},
  nombre: {
    required: helpers.withMessage(
      "El campo nombre no puede estar vacío",
      required
    ),
    maxLength: helpers.withMessage(
      "El nombre no puede superar los 500 caracteres",
      maxLength(500)
    )
  },
  observaciones: {
    maxLength: helpers.withMessage(
      "Las observaciones no pueden superar los 500 caracteres",
      maxLength(500)
    )
  },
  categoriaId: {
    required: helpers.withMessage(
      "Debe seleccionar una categoria",
      (value: any) => {
        return value !== null && value !== undefined && value !== "";
      }
    )
  },
  moneda: {
    required: helpers.withMessage(
      "Debe seleccionar una moneda",
      (value: any) => {
        return value !== null && value !== undefined && value !== "";
      }
    )
  },
  tasaId: {
    required: helpers.withMessage(
      "Debe seleccionar una tasa de iva",
      (value: any) => {
        return value !== null && value !== undefined && value !== "";
      }
    )
  }
};

const validate = useVuelidate(rules, toRefs(formData));

// Si se cambia el producto a editar sin cerrar el modal, precargar los valores correspondientes.
watch(
  () => props.producto,
  p => {
    if (!p) return;
    formData.codigo = p.codigo ?? "";
    formData.nombre = p.nombre ?? "";
    formData.observaciones = p.observaciones ?? "";
    formData.activo = p.activo ?? true;
    formData.categoriaId = p.categoriaId ?? "";
    formData.tasaId = p.tasaIvaId ?? userStore.opcionesGenerales.tasaIVADefecto;
    formData.precioSinIva = p.precioSinIva ?? 0;
    formData.precioConIva = p.precioConIva ?? 0;
    // Nota: moneda se toma por defecto del usuario, ya que el formulario no edita moneda en modo edición.
    formData.moneda = userStore.opcionesGenerales.monedaDefecto;
    const def = userStore.opcionesGenerales.impresionPorDefecto ?? [];
    const impresion = p.impresionSectores?.length ? p.impresionSectores : def;
    formData.impresionSectores = [...impresion];
  },
  { immediate: true }
);

const changePrecioTasa = (precioSinIva: number) => {
  const tasa = tasasStore.getTasaIVAById(formData.tasaId);

  if (tasa != null && !isNaN(precioSinIva) && !isNaN(tasa.valor)) {
    formData.precioConIva = round(precioSinIva * (1 + tasa.valor / 100), 2);
  }
};

const changePrecioConIva = (precioSinIva: number) => {
  formData.precioSinIva = round(precioSinIva, 2);
  const tasa = tasasStore.getTasaIVAById(formData.tasaId);
  if (tasa && !isNaN(precioSinIva) && !isNaN(tasa.valor)) {
    formData.precioConIva = round(precioSinIva * (1 + tasa.valor / 100), 2);
  }
};

const changePrecioSinIva = (precioConIva: number) => {
  formData.precioConIva = round(precioConIva, 2);
  const tasa = tasasStore.getTasaIVAById(formData.tasaId);

  if (tasa != null && !isNaN(precioConIva) && !isNaN(tasa.valor)) {
    formData.precioSinIva = round(precioConIva / (1 + tasa.valor / 100), 2);
  }
};

const onFotoSelected = (event: Event) => {
  const input = event.target as HTMLInputElement;
  fotoFile.value = input?.files?.[0] ?? null;
  // Si selecciona una nueva foto, dejamos de marcar eliminación.
  if (fotoFile.value) quitarFotoMarcado.value = false;
  setFotoPreviewFromFile();
};

const guardar = async () => {
  const result = await validate.value.$validate();
  if (!result) {
    mensajesStore.mostrarModal(
      "Error",
      "Hay errores en el formulario. Verificalos e intentá de nuevo.",
      mensajesStore.mensajeType.error
    );
    return;
  }

  cargando.value = true;
  try {
    const tasa = tasasStore.getTasaIVAById(formData.tasaId);
    const categoria = categoriasStore.getCategoriaById(formData.categoriaId);

    const okUpdate = await productosStore.updateProducto(props.producto.id, {
      nombre: formData.nombre.trim(),
      activo: formData.activo,
      precioConIva: formData.precioConIva,
      precioSinIva: formData.precioSinIva,
      tasaIvaId: formData.tasaId,
      tasaIvaNombre: tasa?.nombre ?? null,
      categoriaId: formData.categoriaId,
      categoriaNombre: categoria?.nombre ?? null,
      codigo: formData.codigo,
      observaciones: formData.observaciones.trim() || null,
      impresionSectores: formData.impresionSectores
    });

    let okFoto = true;
    if (okUpdate) {
      if (fotoFile.value) {
        okFoto = await productosStore.subirFotoProducto(
          props.producto.id,
          formData.nombre,
          fotoFile.value
        );
      } else if (quitarFotoMarcado.value) {
        okFoto = await productosStore.quitarFotoProducto(props.producto.id);
      }
    }

    if (okUpdate && okFoto) {
      mensajesStore.mostrarToast(
        "Producto actualizado",
        `Se actualizó el producto "${formData.nombre}".`,
        mensajesStore.mensajeType.exito
      );

      const logAcciones: LogAcciones = {
        id: "",
        accion: [
          `Se modifica el producto ${props.producto.nombre} (${props.producto.id}).`
        ],
        modulo: "productos",
        fecha: getCurrentTime(),
        usuario: userStore.userLoggedIn?.uid!,
        mail: userStore.userLoggedIn?.email!
      };

      await logAccionesStore.guardarLog(logAcciones);
      // Limpiar preview local cuando se confirma con Guardar.
      if (fotoPreviewObjectUrl.value) {
        URL.revokeObjectURL(fotoPreviewObjectUrl.value);
        fotoPreviewObjectUrl.value = null;
      }
      fotoFile.value = null;
      quitarFotoMarcado.value = false;
      emit("cerrar");
    } else if (!okUpdate) {
      mensajesStore.mostrarModal(
        "Error",
        "No se pudo actualizar el producto. Intentá de nuevo.",
        mensajesStore.mensajeType.error
      );
    } else if (!okFoto) {
      mensajesStore.mostrarModal(
        "Error",
        "Se actualizó el producto pero no se pudo subir la nueva foto.",
        mensajesStore.mensajeType.error
      );
    }
  } finally {
    cargando.value = false;
  }
};

const quitarFoto = () => {
  // Solo marca la foto como "para eliminar" hasta que el usuario confirme con "Guardar".
  quitarFotoMarcado.value = true;
  fotoFile.value = null;
  // Ocultar preview local si existía.
  if (fotoPreviewObjectUrl.value) {
    URL.revokeObjectURL(fotoPreviewObjectUrl.value);
    fotoPreviewObjectUrl.value = null;
  }
};

const habilitarEdicion = (e: any) => {
  e.target.removeAttribute("readonly");
  e.target.focus();
};
</script>

<template>
  <Dialog :open="true" @close="() => emit('cerrar')" size="xl">
    <Dialog.Panel class="max-h-[70vh] overflow-y-auto">
      <Dialog.Title>
        <h2 class="mr-auto text-base font-medium">
          Editar producto: {{ producto.nombre }}
        </h2>
      </Dialog.Title>

      <Dialog.Description>
        <!-- Nombre -->
        <div class="flex mt-5">
          <div class="flex-none text-right pr-5 mt-2">
            <FormLabel class="w-20 md:w-32\ sw:w-20">Nombre</FormLabel>
          </div>
          <div class="flex-1">
            <FormInput
              type="text"
              v-model.trim="validate.nombre.$model"
              :class="{
                'border-danger': validate.nombre.$error
              }"
              placeholder="Nombre"
            />
          </div>
        </div>

        <!-- Categoría -->
        <div class="flex mt-5">
          <div class="flex-none text-right pr-5 mt-2">
            <FormLabel class="w-20 md:w-32\ sw:w-20">Categoría</FormLabel>
          </div>
          <div class="flex-1">
            <TomSelect
              v-model="formData.categoriaId"
              :class="{
                'border-danger': validate.categoriaId.$error
              }"
              placeholder="Seleccione categoría"
            >
              <option v-if="formData.categoriaId" :value="formData.categoriaId">
                {{
                  categoriasStore.getCategoriaById(formData.categoriaId)?.nombre
                }}
              </option>
              <option v-else :value="formData.categoriaId">Seleccionar</option>
              <option
                v-for="rubro in categoriasStore.categorias"
                :key="rubro.id"
                :value="rubro.id"
              >
                {{ rubro.nombre }}
              </option>
            </TomSelect>
          </div>
        </div>

        <!-- Sectores de impresión -->
        <div
          class="flex mt-5"
          v-if="
            userStore.opcionesGenerales && userStore.opcionesGenerales.sectores
          "
        >
          <div class="flex">
            <div class="flex-none text-right pr-5 mt-2">
              <FormLabel class="w-20 md:w-32\ sw:w-20"
                >Sectores de impresión</FormLabel
              >
            </div>
            <div class="flex-1">
              <div class="flex flex-wrap gap-3">
                <FormCheck
                  v-for="sector in userStore.opcionesGenerales.sectores"
                  :key="sector"
                  class="flex items-center mt-2"
                >
                  <FormCheck.Input
                    type="checkbox"
                    :id="`sector-editar-${sector}`"
                    :value="sector"
                    v-model="formData.impresionSectores"
                  />
                  <FormCheck.Label
                    :for="`sector-editar-${sector}`"
                    class="ml-1"
                  >
                    {{ sector }}
                  </FormCheck.Label>
                </FormCheck>
              </div>
              <p class="mt-1 text-xs text-slate-500">
                Podés seleccionar ninguno, uno o varios sectores.
              </p>
            </div>
          </div>
        </div>

        <!-- Foto actual y cambio -->
        <div class="flex mt-5">
          <div class="flex-none text-right pr-5 mt-2">
            <FormLabel class="w-20 md:w-32\ sw:w-20">Cambiar foto</FormLabel>
            <input
              id="producto-editar-foto"
              type="file"
              accept="image/*"
              class="hidden"
              @change="onFotoSelected"
            />
            <label
              for="producto-editar-foto"
              class="ml-5 inline-flex items-center justify-center text-sm font-semibold text-slate-500 cursor-pointer select-none rounded-md border border-slate-200 bg-primary text-white py-2 px-4 text-center hover:bg-primary/90"
            >
              Adjuntar imagen
            </label>
          </div>

          <div class="flex items-center gap-4">
            <div
              class="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded border border-slate-200 bg-slate-100 dark:border-darkmode-400 dark:bg-darkmode-600"
            >
              <img
                v-if="fotoUrlPreview"
                :src="fotoUrlPreview"
                :alt="productoActual.nombre"
                class="h-full w-full object-cover"
              />
              <Lucide v-else icon="Image" class="h-10 w-10 text-slate-400" />
            </div>
            <span v-if="!fotoUrlPreview" class="text-sm text-slate-500"
              >Sin foto</span
            >
          </div>

          <div class="mt-3">
            <Button
              v-if="fotoUrlPreview"
              variant="outline-secondary"
              size="sm"
              :disabled="cargando"
              @click="quitarFoto"
            >
              Quitar foto
            </Button>
          </div>
        </div>
        <!-- Precio -->
        <div class="flex mt-5">
          <div class="flex-none text-right pr-5 mt-2">
            <FormLabel class="w-20 md:w-32\ sw:w-20"
              >Precio
              {{
                monedasStore.getMonedaById(
                  userStore.opcionesGenerales.monedaDefecto
                )?.simbolo
              }}
            </FormLabel>
          </div>
          <div class="flex-1">
            <InputNumero
              v-model:valor="formData.precioConIva"
              @change="
                (valor: number) => {
                  changePrecioSinIva(valor);
                }
              "
              :precision="2"
              :align="`left`"
              :autoSeleccionar="true"
              :class="{
                'border-danger': validate.$error
              }"
              placeholder="Precio con iva"
            />
          </div>
        </div>

        <!-- Precios -->
        <!-- <div class="mr-auto text-sm font-medium mt-5 overflow-x-auto">
          <Table>
            <Table.Thead>
              <Table.Tr>
                <Table.Th class="min-w-28">Precio</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              <Table.Tr>
                <Table.Td
                  v-if="tasasStore.getTasaIVAById(formData.tasaId) != null"
                >
                  <InputNumero
                    v-model:valor="formData.precioConIva"
                    @change="
                      (valor: number) => {
                        changePrecioSinIva(valor);
                      }
                    "
                    :precision="2"
                    :autoSeleccionar="true"
                    :class="{
                      'border-danger': validate.$error
                    }"
                    placeholder="Precio con iva"
                  />
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
        </div> -->

        <!-- Observaciones -->
        <div class="flex mt-5">
          <div class="flex-none text-right pr-5 mt-2">
            <FormLabel class="w-20 md:w-32\ sw:w-20" for="producto-editar-observaciones"
              >Observaciones</FormLabel
            >
          </div>
          <div class="flex-1">
            <FormTextarea
              id="producto-editar-observaciones"
              v-model="validate.observaciones.$model"
              rows="3"
              :class="{
                'border-danger': validate.observaciones.$error
              }"
              placeholder="Observaciones..."
            />
            <template v-if="validate.observaciones.$error">
              <div
                v-for="(error, index) in validate.observaciones.$errors"
                :key="index"
                class="mt-2 text-danger"
              >
                {{ error.$message }}
              </div>
            </template>
          </div>
        </div>

        <!-- Activo -->
        <div class="flex mt-5">
          <div class="flex-none text-right pr-5 mt-2">
            <FormLabel class="w-20 md:w-32\ sw:w-20">Estado</FormLabel>
          </div>
          <div class="flex-1 mt-2">
            <FormSwitch class="flex items-center">
              <FormSwitch.Input
                id="producto-editar-activo"
                type="checkbox"
                v-model="formData.activo"
              />
              <FormSwitch.Label class="ml-2">
                {{ formData.activo ? "Activo" : "Inactivo" }}
              </FormSwitch.Label>
            </FormSwitch>
          </div>
        </div>

        <!-- Validación final -->
        <Alert
          variant="outline-danger"
          class="flex items-center my-4"
          v-if="validate?.$error"
        >
          <Lucide icon="AlertCircle" class="w-6 h-6 mr-2" />
          <ul>
            <li v-for="e in validate.$errors">{{ e.$message }}</li>
          </ul>
        </Alert>
      </Dialog.Description>

      <Dialog.Footer>
        <div class="flex text-right" v-if="!cargando">
          <div class="flex-1 text-left">
            <Button
              variant="outline-secondary"
              :disabled="cargando"
              @click="() => emit('cerrar')"
            >
              Cerrar
            </Button>
          </div>
          <div class="flex-1">
            <Button variant="primary" :disabled="cargando" @click="guardar">
              Guardar
            </Button>
          </div>
        </div>
        <div v-else class="flex my-6">
          <div class="mx-auto">
            <LoadingIcon icon="oval" class="w-8 h-8" />
          </div>
        </div>
      </Dialog.Footer>
    </Dialog.Panel>
  </Dialog>
</template>
